/**
 * <FeaturePage> — shared marketing template for any feature landing page.
 *
 * Driven entirely by `featuresContent.js`. Visual language matches the
 * homepage Hero (same fonts, gradients, button styles, lead-capture form).
 * One file → 6 pages.
 */
import React, { useState } from 'react';
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { CheckCircle2, ArrowRight, Quote, Sparkles } from 'lucide-react';
import { featurePages, featureList } from '../data/featuresContent';
import Navbar from './Navbar';
import Footer from './Footer';
import { track } from '../services/analytics';
import { toast } from '../hooks/use-toast';
import PageMeta from './PageMeta';
import StickyMobileCTA from './StickyMobileCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Accent colour palette per feature page — keeps each page visually distinct
// while sharing the same overall design language.
const ACCENTS = {
  green:  { ring: 'ring-green-200',  text: 'text-green-700',  bg: 'bg-green-50',  btn: 'bg-green-600 hover:bg-green-700',   dot: 'bg-green-500'  },
  blue:   { ring: 'ring-blue-200',   text: 'text-blue-700',   bg: 'bg-blue-50',   btn: 'bg-blue-600 hover:bg-blue-700',     dot: 'bg-blue-500'   },
  orange: { ring: 'ring-orange-200', text: 'text-orange-700', bg: 'bg-orange-50', btn: 'bg-orange-600 hover:bg-orange-700', dot: 'bg-orange-500' },
  purple: { ring: 'ring-purple-200', text: 'text-purple-700', bg: 'bg-purple-50', btn: 'bg-purple-600 hover:bg-purple-700', dot: 'bg-purple-500' },
  amber:  { ring: 'ring-amber-200',  text: 'text-amber-700',  bg: 'bg-amber-50',  btn: 'bg-amber-600 hover:bg-amber-700',   dot: 'bg-amber-500'  },
};

const FeaturePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [name,   setName]   = useState('');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feature = featurePages[slug];
  if (!feature) return <Navigate to="/" replace />;

  const accent = ACCENTS[feature.heroAccent] || ACCENTS.orange;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test((mobile || '').replace(/\D/g, '').slice(-10))) {
      toast({ title: 'Please enter a valid 10-digit mobile', variant: 'destructive' });
      return;
    }
    setLoading(true);
    track('cta_feature_demo_submitted', { source: feature.ctaSource, slug: feature.slug });
    try {
      await axios.post(`${API}/demo-requests`, {
        name: name || null,
        mobile,
        source: feature.ctaSource,
        property_type: feature.eyebrow,
        message: `Lead from /features/${feature.slug} landing page`,
      });
      setSubmitted(true);
      toast({ title: 'Got it!', description: 'Our team will reach out within an hour.' });
    } catch (err) {
      toast({ title: 'Could not submit', description: err.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Related feature suggestions (everything except the current one)
  const others = featureList.filter(f => f.slug !== feature.slug).slice(0, 3);

  // Absolute URLs for OG, canonical, and JSON-LD (must be public-facing).
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://rentkollect.com';
  const pageUrl = `${origin}/features/${feature.slug}`;
  const ogImage = `${origin}/api/og/feature/${feature.slug}.png`;

  // SoftwareApplication JSON-LD — helps Google rich snippets + gives AI
  // crawlers (Claude/GPT/Perplexity/Gemini) a structured fact sheet to ground
  // their answers on.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `RentKollect ${feature.eyebrow}`,
    headline: feature.title,
    description: feature.subtitle,
    url: pageUrl,
    image: ogImage,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Property Management Software',
    operatingSystem: 'Web, iOS, Android',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
      description: '30-day free trial, no credit card required',
    },
    publisher: {
      '@type': 'Organization',
      name: 'RentKollect',
      url: 'https://rentkollect.com',
      logo: `${origin}/app-icon.png`,
    },
    featureList: feature.bullets.map(b => `${b.title}: ${b.desc}`),
  };

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${feature.title} — RentKollect`}
        description={feature.subtitle}
        path={`/features/${feature.slug}`}
        ogImage={ogImage}
        canonical={pageUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      {/* ---------- Hero ---------- */}
      <section className={`relative pt-24 pb-16 ${accent.bg}`} data-testid={`feature-hero-${feature.slug}`}>
        {/* Subtle dot grid for texture, same as homepage hero */}
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${accent.text} bg-white/70 backdrop-blur px-3 py-1 rounded-full ring-1 ${accent.ring}`}>
              <Sparkles size={12} /> {feature.eyebrow}
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight">
              {feature.title}
            </h1>
            <p className="mt-5 text-lg text-gray-700 max-w-xl leading-relaxed">{feature.subtitle}</p>

            {/* Lead-capture form (same layout as homepage hero) */}
            {!submitted ? (
              <form onSubmit={onSubmit} className="mt-7 max-w-md" data-testid={`feature-form-${feature.slug}`}>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white"
                    data-testid="feature-form-name"
                  />
                  <Input
                    type="tel"
                    placeholder="WhatsApp number"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                    className="bg-white"
                    required
                    data-testid="feature-form-mobile"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className={`${accent.btn} text-white whitespace-nowrap`}
                    data-testid="feature-form-submit"
                  >
                    {loading ? 'Sending…' : feature.ctaLabel}
                    {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-gray-500">No spam. We&apos;ll WhatsApp you within an hour.</p>
              </form>
            ) : (
              <div className="mt-7 p-4 rounded-xl bg-white ring-1 ring-green-200 max-w-md flex items-start gap-3" data-testid={`feature-success-${feature.slug}`}>
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">Got it — we&apos;ll reach out within an hour.</p>
                  <p className="text-sm text-gray-600 mt-0.5">In the meantime, you can already <Link to="/manager/login" className="underline text-blue-700">start a free trial</Link>.</p>
                </div>
              </div>
            )}
          </div>

          {/* Right column — visual stack of feature bullets */}
          <div className="relative hidden lg:block">
            <div className="grid gap-3">
              {feature.bullets.slice(0, 3).map((b) => {
                const Icon = b.icon;
                return (
                  <Card key={b.title} className={`p-4 border-0 ring-1 ${accent.ring} bg-white/80 backdrop-blur shadow-sm`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${accent.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={18} className={accent.text} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{b.title}</h3>
                        <p className="text-xs text-gray-600 mt-0.5 leading-snug">{b.desc}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Detailed bullets ---------- */}
      <section className="py-20 bg-white" data-testid={`feature-bullets-${feature.slug}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Everything you get</h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
              Designed for Indian PG and co-living owners. Live on day one. No setup fee.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {feature.bullets.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  data-testid={`feature-bullet-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`p-5 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all bg-white`}
                >
                  <div className={`w-11 h-11 rounded-lg ${accent.bg} flex items-center justify-center mb-3`}>
                    <Icon size={20} className={accent.text} />
                  </div>
                  <h3 className="font-bold text-gray-900 leading-tight">{b.title}</h3>
                  <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Social proof ---------- */}
      {feature.socialProof && (
        <section className={`py-16 ${accent.bg}`}>
          <div className="max-w-3xl mx-auto px-4 text-center">
            <Quote className={`${accent.text} mx-auto mb-4`} size={32} />
            <blockquote className="text-xl sm:text-2xl text-gray-900 font-medium leading-snug">
              {feature.socialProof}
            </blockquote>
          </div>
        </section>
      )}

      {/* ---------- Related features ---------- */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Explore more</h3>
          <p className="text-gray-600 mb-8">RentKollect is one complete platform. Here&apos;s what else is in the box.</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {others.map((o) => {
              const oAccent = ACCENTS[o.heroAccent] || ACCENTS.orange;
              return (
                <Link
                  key={o.slug}
                  to={`/features/${o.slug}`}
                  data-testid={`related-feature-${o.slug}`}
                  className={`block p-5 rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all`}
                >
                  <span className={`text-xs font-bold uppercase tracking-wider ${oAccent.text}`}>{o.eyebrow}</span>
                  <h4 className="mt-2 font-bold text-gray-900 leading-tight">{o.title}</h4>
                  <span className={`mt-3 inline-flex items-center text-sm font-medium ${oAccent.text}`}>
                    Learn more <ArrowRight size={14} className="ml-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className={`py-20 ${accent.bg}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Ready to {feature.eyebrow.toLowerCase()}?</h2>
          <p className="mt-3 text-gray-700">Free for the first 30 days. No credit card. Cancel anytime.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/manager/login')}
              className={`${accent.btn} text-white px-8`}
              data-testid={`feature-bottom-cta-trial-${feature.slug}`}
            >
              Start free trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                document.querySelector(`[data-testid="feature-form-${feature.slug}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                track('cta_feature_bottom_demo_clicked', { source: feature.ctaSource });
              }}
              className="bg-white"
              data-testid={`feature-bottom-cta-demo-${feature.slug}`}
            >
              Book a 15-min demo
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <StickyMobileCTA
        slug={feature.slug}
        eyebrow={feature.eyebrow}
        ctaSource={feature.ctaSource}
        accentClasses={accent.btn}
      />
    </div>
  );
};

export default FeaturePage;
